import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Users,
  TrendingUp,
  TrendingDown,
  Activity,
  MessageSquare,
  DollarSign,
  Zap,
  RefreshCw,
  PieChart,
  Clock,
  Filter,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { apiClient } from '@/lib/api/client'

// Types
interface UsageStats {
  users: {
    total: number
    verified: number
    paid: number
    by_tier: Record<string, number>
    new_7d: { current: number; previous: number | null; change_pct: number | null }
  }
  engagement: {
    dau: { current: number; previous: number | null; change_pct: number | null }
    wau: { current: number; previous: number | null; change_pct: number | null }
    mau: { current: number; previous: number | null; change_pct: number | null }
  }
  tool_usage: {
    today: {
      calls: number
      success_rate: number
      avg_response_ms: number | null
      previous?: number | null
      change_pct?: number | null
    }
    week: {
      calls: number
      success_rate: number
      avg_response_ms: number | null
      previous: number | null
      change_pct: number | null
    }
    month: {
      calls: number
      success_rate: number
      avg_response_ms: number | null
      previous?: number | null
      change_pct?: number | null
    }
    top_tools: Array<{ name: string; calls: number; success_rate: number }>
  }
  chat: {
    conversations: number
    users_with_chats: number
    total_messages: number
    user_messages_7d: number
    avg_messages_per_conversation: number
  }
  revenue: {
    mrr: number
    active_subscriptions: number
    churned_30d: number
    churn_rate: number
    arpu: number
    total_revenue: number
    call_pack_revenue: number
  }
}

// API function
const fetchUsageStats = async (): Promise<UsageStats> => {
  const response = await apiClient.get('/admin/stats/usage')
  return response.data
}

// Range Stats Types
interface RangeStats {
  range_hours: number
  range_label: string
  period_start: string
  period_end: string
  new_users: number
  new_users_by_tier: Array<{ tier: string; count: number }>
  tool_calls: number
  tool_success_rate: number
  top_tools: Array<{ tool_name: string; calls: number; success_rate: number; unique_users: number }>
  unique_tool_users: number
  new_conversations: number
  new_messages: number
  active_chatters: number
}

const fetchRangeStats = async (hours: number): Promise<RangeStats> => {
  const response = await apiClient.get(`/admin/stats/range?hours=${hours}`)
  return response.data
}

const RANGE_OPTIONS = [
  { value: 1, label: '1 hour' },
  { value: 6, label: '6 hours' },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '2 days' },
  { value: 168, label: '1 week' },
  { value: 336, label: '2 weeks' },
]

// Change Badge Component
const ChangeBadge = ({
  value,
  suffix = '%',
}: {
  value: number | null | undefined
  suffix?: string
}) => {
  if (value === null || value === undefined || value === 0) return null
  const isPositive = value > 0
  return (
    <Badge
      variant="outline"
      className={`ml-2 ${isPositive ? 'text-green-600 border-green-300' : 'text-red-600 border-red-300'}`}
    >
      {isPositive ? (
        <TrendingUp className="h-3 w-3 mr-1" />
      ) : (
        <TrendingDown className="h-3 w-3 mr-1" />
      )}
      {isPositive ? '+' : ''}
      {value.toFixed(1)}
      {suffix}
    </Badge>
  )
}

// Stat Card Component
const StatCard = ({
  title,
  value,
  subtitle,
  change,
  icon: Icon,
}: {
  title: string
  value: string | number
  subtitle?: string
  change?: number | null
  icon?: React.ComponentType<{ className?: string }>
}) => (
  <Card>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
    </CardHeader>
    <CardContent>
      <div className="flex items-center">
        <div className="text-2xl font-bold">{value}</div>
        {change !== undefined && <ChangeBadge value={change} />}
      </div>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
)

// Tier Distribution Component
const TierDistribution = ({ byTier }: { byTier: Record<string, number> }) => {
  const total = Object.values(byTier).reduce((a, b) => a + b, 0)
  const colors: Record<string, string> = {
    free: 'bg-gray-400',
    enthusiast: 'bg-blue-500',
    professional: 'bg-purple-600',
    business: 'bg-amber-500',
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PieChart className="h-4 w-4" />
          Users by Tier
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {Object.entries(byTier).map(([tier, count]) => (
          <div key={tier} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="capitalize">{tier}</span>
              <span className="text-muted-foreground">
                {count} ({((count / total) * 100).toFixed(0)}%)
              </span>
            </div>
            <Progress
              value={(count / total) * 100}
              className={`h-2 ${colors[tier] || 'bg-gray-400'}`}
            />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// Top Tools Component
const TopTools = ({
  tools,
}: {
  tools: Array<{ name: string; calls: number; success_rate: number }>
}) => {
  if (!tools || tools.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Zap className="h-4 w-4" />
            Top Tools (7 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No tool usage data available</p>
        </CardContent>
      </Card>
    )
  }

  const maxCalls = Math.max(...tools.map((t) => t.calls))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Zap className="h-4 w-4" />
          Top Tools (7 days)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {tools.slice(0, 5).map((tool) => (
          <div key={tool.name} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="font-mono text-xs">{tool.name}</span>
              <span className="text-muted-foreground">{tool.calls} calls</span>
            </div>
            <div className="flex items-center gap-2">
              <Progress value={(tool.calls / maxCalls) * 100} className="h-2 flex-1" />
              <span className="text-xs text-muted-foreground w-12">
                {tool.success_rate.toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

// Custom Range Section Component
const CustomRangeSection = () => {
  const [selectedHours, setSelectedHours] = useState(6)

  const {
    data: rangeStats,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-range-stats', selectedHours],
    queryFn: () => fetchRangeStats(selectedHours),
    refetchInterval: 60000,
  })

  const tierColors: Record<string, string> = {
    free: 'bg-gray-400',
    enthusiast: 'bg-blue-500',
    professional: 'bg-purple-600',
    business: 'bg-amber-500',
  }

  return (
    <Card className="border-primary/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Custom Time Range
          </CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={selectedHours}
              onChange={(e) => setSelectedHours(Number(e.target.value))}
              className="text-sm border rounded-md px-2 py-1 bg-background"
            >
              {RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {rangeStats && (
          <CardDescription>
            {rangeStats.range_label} • {new Date(rangeStats.period_start).toLocaleString()} to now
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error || !rangeStats ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            Failed to load range statistics
          </p>
        ) : (
          <div className="space-y-6">
            {/* New Users */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Users className="h-4 w-4" />
                New Users ({rangeStats.new_users})
              </h4>
              {rangeStats.new_users_by_tier.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {rangeStats.new_users_by_tier.map(({ tier, count }) => (
                    <div key={tier} className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                      <div
                        className={`w-3 h-3 rounded-full ${tierColors[tier] || 'bg-gray-400'}`}
                      />
                      <span className="text-sm capitalize">{tier}</span>
                      <span className="ml-auto font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No new users in this period</p>
              )}
            </div>

            {/* Tool Usage */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Tool Usage
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.tool_calls}</div>
                  <div className="text-xs text-muted-foreground">Total Calls</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.tool_success_rate}%</div>
                  <div className="text-xs text-muted-foreground">Success Rate</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.unique_tool_users}</div>
                  <div className="text-xs text-muted-foreground">Unique Users</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.top_tools.length}</div>
                  <div className="text-xs text-muted-foreground">Tools Used</div>
                </div>
              </div>
              {rangeStats.top_tools.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Top tools:</p>
                  {rangeStats.top_tools.slice(0, 5).map((tool) => (
                    <div key={tool.tool_name} className="flex items-center gap-2 text-sm">
                      <span className="font-mono text-xs flex-1 truncate">{tool.tool_name}</span>
                      <span className="text-muted-foreground">{tool.calls} calls</span>
                      <span className="text-muted-foreground">• {tool.unique_users} users</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chat Activity */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Chat Activity
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.new_conversations}</div>
                  <div className="text-xs text-muted-foreground">New Conversations</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.new_messages}</div>
                  <div className="text-xs text-muted-foreground">New Messages</div>
                </div>
                <div className="p-3 rounded-md bg-muted/50">
                  <div className="text-2xl font-bold">{rangeStats.active_chatters}</div>
                  <div className="text-xs text-muted-foreground">Active Conversations</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export function UsageStatsTab() {
  const {
    data: stats,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['admin-usage-stats'],
    queryFn: fetchUsageStats,
    refetchInterval: 60000, // Refresh every minute
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error || !stats) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <p>Failed to load usage statistics</p>
        <button onClick={() => refetch()} className="text-primary underline mt-2">
          Try again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Users Section */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5" />
          Users Overview
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard title="Total Users" value={stats.users.total} icon={Users} />
          <StatCard
            title="Verified"
            value={stats.users.verified}
            subtitle={`${((stats.users.verified / stats.users.total) * 100).toFixed(0)}% of total`}
          />
          <StatCard
            title="Paid Users"
            value={stats.users.paid}
            subtitle={`${((stats.users.paid / stats.users.total) * 100).toFixed(1)}% conversion`}
          />
          <StatCard
            title="New (7 days)"
            value={stats.users.new_7d.current}
            change={stats.users.new_7d.change_pct}
          />
        </div>
      </div>

      {/* Custom Time Range Section */}
      <CustomRangeSection />

      {/* Tier Distribution + Engagement */}
      <div className="grid gap-4 md:grid-cols-2">
        <TierDistribution byTier={stats.users.by_tier} />

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{stats.engagement.dau.current}</div>
                <div className="text-xs text-muted-foreground">DAU</div>
                <ChangeBadge value={stats.engagement.dau.change_pct} />
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{stats.engagement.wau.current}</div>
                <div className="text-xs text-muted-foreground">WAU</div>
                <ChangeBadge value={stats.engagement.wau.change_pct} />
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold">{stats.engagement.mau.current}</div>
                <div className="text-xs text-muted-foreground">MAU</div>
                <ChangeBadge value={stats.engagement.mau.change_pct} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tool Usage */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Tool Usage
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Today"
            value={stats.tool_usage.today.calls}
            subtitle={`${stats.tool_usage.today.success_rate.toFixed(1)}% success`}
            icon={Zap}
          />
          <StatCard
            title="This Week"
            value={stats.tool_usage.week.calls}
            change={stats.tool_usage.week.change_pct}
            subtitle={`${stats.tool_usage.week.success_rate.toFixed(1)}% success`}
          />
          <StatCard
            title="This Month"
            value={stats.tool_usage.month.calls}
            subtitle={`${stats.tool_usage.month.success_rate.toFixed(1)}% success`}
          />
          <StatCard
            title="Avg Response"
            value={
              stats.tool_usage.today.avg_response_ms != null
                ? `${stats.tool_usage.today.avg_response_ms}ms`
                : '—'
            }
            subtitle="Today's average"
          />
        </div>
      </div>

      {/* Top Tools + Chat */}
      <div className="grid gap-4 md:grid-cols-2">
        <TopTools tools={stats.tool_usage.top_tools} />

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Chat Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-2xl font-bold">{stats.chat.conversations}</div>
                <div className="text-xs text-muted-foreground">Conversations</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.chat.users_with_chats}</div>
                <div className="text-xs text-muted-foreground">Users with chats</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.chat.total_messages}</div>
                <div className="text-xs text-muted-foreground">Total messages</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{stats.chat.user_messages_7d}</div>
                <div className="text-xs text-muted-foreground">Messages (7d)</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Revenue */}
      <div>
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Revenue
        </h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <StatCard title="MRR" value={`$${stats.revenue.mrr.toFixed(2)}`} icon={DollarSign} />
          <StatCard title="Active Subscriptions" value={stats.revenue.active_subscriptions} />
          <StatCard
            title="Churn Rate"
            value={`${stats.revenue.churn_rate.toFixed(1)}%`}
            subtitle={`${stats.revenue.churned_30d} churned (30d)`}
          />
          <StatCard title="ARPU" value={`$${stats.revenue.arpu.toFixed(2)}`} />
          <StatCard
            title="Total Revenue"
            value={`$${stats.revenue.total_revenue.toFixed(2)}`}
            subtitle={`Call packs: $${stats.revenue.call_pack_revenue.toFixed(2)}`}
          />
        </div>
      </div>
    </div>
  )
}
